import ErrorCircleSvg from "@/src/assets/icons/icon-error-circle.svg";
import EyeOffSvg from "@/src/assets/icons/icon-eye-off.svg";
import EyeSvg from "@/src/assets/icons/icon-eye.svg";
import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button } from "@/src/components/atom/Button";
import TextButton from "@/src/components/atom/Button/TextButton";
import Checkbox from "@/src/components/atom/Checkbox";
import Icon from "@/src/components/atom/Icon";
import Input from "@/src/components/atom/Input";
import IconInput from "@/src/components/atom/Input/IconInput";
import Typo from "@/src/components/atom/Typo";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import AutoLogoutAlertDialog from "@/src/components/organism/AutoLogoutAlertDialog";
import {
  LOCKED_ACCOUNT_CODE,
  ACCOUNT_INACTIVATED_CODE,
  SUBSCRIPTION_EMPTY_LIST_ERROR_CODE,
  EXPORTER_USER_EXPORTER_INACTIVATED_CODE,
  EXPORTER_USER_SUBSCRIPTION_NOT_AVAILABLE_CODE,
} from "@/src/constant/error";
import { localstorageKeySet } from "@/src/constant/localstorage";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import useRedirectNavigate from "@/src/hooks/useRedirectNavigate";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import PRIVATE_PATH from "@/src/routes/private/path";
import PUBLIC_PATH from "@/src/routes/public/path";
import { useAppDispatch, useAppSelector } from "@/src/store";
import {
  useEditLanguageMutation,
  usePendingResetPasswordMutation,
  useSignInMutation,
  useLazyGetExporterUserAdditionalInfosQuery,
} from "@/src/store/apis/auth";
import { ExporterUserDto } from "@/src/store/apis/auth/interface";
import { useLazyGetCompanyLinkagePendingListQuery } from "@/src/store/apis/link";
import {
  useLazyGetCurrentSubscriptionQuery,
} from "@/src/store/apis/subscription";
import { setMfaUser, setRedirectUrl, setTempUser, setUser, setLoginRedirectComplete } from "@/src/store/slice/auth";
import { openLinkageRequestAlertDialog } from "@/src/store/slice/webUtil";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { isDevelopment } from "@/src/utils/environment";
import { extractErrorMessage } from "@/src/utils/errorUtils";
import { emailRegex } from "@/src/utils/regex";
import { utcNow } from "@/src/utils/utcDayjs";
import {
  isCorporateManagerWithSellerAccess,
  isSubscriptionCanceledOrFinished,
  isFreeTrialSubscription,
  getHomePathByCompanyType,
} from "./utils";
import { USER_TYPE } from "@/src/constant/login";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NavigateOptions, useNavigate } from "react-router-dom";
import { css, styled } from "styled-components";

enum ALERT_MODAL_STATUS {
  NONE,
  PASSWORD_CHANGE_90DAYS_AFTER,
  LOCKED_ACCOUNT,
  AUTO_LOGOUT,
}

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const { tempUser } = useAppSelector((state) => state.auth);

  const idInputRef = useRef<HTMLInputElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const redirectOrNavigate = useRedirectNavigate();
  const { handleContentLoadingOff } = useContentLoading();

  const isAutoLogoutDialogOpen = localStorage.getItem(
    localstorageKeySet.IS_SESSION_TIME_OUT
  );

  // API
  const [editLanguage, { isLoading: isEditUser }] = useEditLanguageMutation();
  const [signIn, { isLoading: isSigningIn }] = useSignInMutation();
  const [resetPassword] = usePendingResetPasswordMutation();
  const [getPendingLinkageList] = useLazyGetCompanyLinkagePendingListQuery();
  const [getCurrentSubscription] = useLazyGetCurrentSubscriptionQuery();
  const [getExporterUserAdditionalInfos] = useLazyGetExporterUserAdditionalInfosQuery();

  const [alertModalState, setAlertModalState] = useState<ALERT_MODAL_STATUS>(
    ALERT_MODAL_STATUS.NONE
  );
  const [isRememberId, setIsRememberId] = useState<boolean>(
    !!localStorage.getItem(localstorageKeySet.ECOYA_ID)
  );
  const [passwordInputType, setPasswordInputType] = useState<
    "password" | "text"
  >("password");

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      id: localStorage.getItem(localstorageKeySet.ECOYA_ID) ?? "",
      password: "",
    },
  });

  const redirectUserByType = async (res: ExporterUserDto) => {
    const companyType =
      res.exporterUserType === USER_TYPE.CORPORATE_MANAGER
        ? res.exporter.companyType
        : res.exporterUserMainFieldType;

    const homePath = getHomePathByCompanyType(
      companyType,
      EXPORTER_PRIVATE_PATH.HOME,
      IMPORTER_PRIVATE_PATH.HOME,
    );
    const navigateHome = () => redirectOrNavigate(homePath);
    const isSellerAccessManager = isCorporateManagerWithSellerAccess(
      res.exporterUserType,
      companyType,
    );

    const redirectToPlan = () => {
      dispatch(setRedirectUrl(null));
      dispatch(setLoginRedirectComplete(false));
      navigate(EXPORTER_PRIVATE_PATH.SUBSCRIPTION_PLAN, { replace: true });
    };

    const redirectToOnboarding = () => {
      dispatch(setRedirectUrl(null));
      dispatch(setLoginRedirectComplete(false));
      navigate(EXPORTER_PRIVATE_PATH.FREE_SIGNUP_COMPLETED, { replace: true });
    };

    try {
      const subscription = await getCurrentSubscription().unwrap();

      dispatch(setUser(res));
      dispatch(setTempUser(null));

      const isEndedSubscription = isSubscriptionCanceledOrFinished(
        subscription.subscriptionStatus,
      );
      const isFreeTrialUser = isFreeTrialSubscription(
        subscription.subscriptionType,
        subscription.plan?.name,
      );

      try {
        const additionalInfo = await getExporterUserAdditionalInfos().unwrap();

        if (
          isSellerAccessManager &&
          additionalInfo.row.isInitialGuideNeeded &&
          isFreeTrialUser &&
          !isEndedSubscription
        ) {
          redirectToOnboarding();
          return;
        }

        if (isSellerAccessManager && isEndedSubscription) {
          redirectToPlan();
          return;
        }
      } catch (error) {
        if (isDevelopment) {
          console.error("Additional info 조회 실패:", error);
        }

        if (isSellerAccessManager && isEndedSubscription) {
          redirectToPlan();
          return;
        }
      }

      dispatch(setLoginRedirectComplete(true));
      return navigateHome();
    } catch (e: any) {
      if (e?.data?.errorCode === SUBSCRIPTION_EMPTY_LIST_ERROR_CODE) {
        dispatch(setLoginRedirectComplete(true));
        if (res.exporterUserType === USER_TYPE.CORPORATE_MANAGER) {
          return redirectOrNavigate(EXPORTER_PRIVATE_PATH.SUBSCRIPTION_LANDING);
        }
        return navigateHome();
      }

      const message = Array.isArray(e?.data?.message)
        ? e?.data?.message[0]
        : e?.data?.message;

      alert.showAlert({
        message,
        type: "error",
      });
    }
  };


  const goTo = ({
    path,
    option,
  }: {
    path: keyof typeof PUBLIC_PATH | keyof typeof PRIVATE_PATH;
    option?: NavigateOptions;
  }) => {
    navigate({ ...PRIVATE_PATH, ...PUBLIC_PATH }[path], option);
  };

  const handleLoginClick = async () => {
    const watchId = getValues("id");
    const password = getValues("password");

    try {
      const { statusCode, row } = await signIn({
        aId: watchId,
        password,
      }).unwrap();

      if (isRememberId) {
        localStorage.setItem(localstorageKeySet.ECOYA_ID, watchId);
      } else {
        localStorage.setItem(localstorageKeySet.ECOYA_ID, "");
      }

      if (statusCode === 202) {
        dispatch(setMfaUser(row));
        return goTo({
          path: "TWO_FACTOR_AUTHENTICATION",
        });
      }

      if (statusCode === 201 && 'hasExporterUser' in row && !row.hasExporterUser) {
        const { accountDto, companyExists, exporter } = row;

        dispatch(setUser(row));

        if (!accountDto.isActivated) {
          navigate(PUBLIC_PATH.SIGNUP_PENDING_APPROVAL);
          return;
        }

        if (companyExists) {
          navigate(PUBLIC_PATH.SIGNUP_MEMBER, {
            state: {
              companyName: exporter?.companyName,
              email: accountDto.email,
              name: accountDto.name,
            }
          });
        } else {
          navigate(PUBLIC_PATH.SIGNUP, { replace: true });
        }
        return;
      }

      // ExporterUser 로그인 케이스: 비활성화 체크를 API 호출 전에 수행
      if (statusCode === 201 && "account" in row && !row.account.isActivated) {
        dispatch(setUser(row));
        navigate(PUBLIC_PATH.SIGNUP_PENDING_APPROVAL);
        return;
      }

      const { data } = await getPendingLinkageList();

      if (data && data.count > 0) {
        dispatch(openLinkageRequestAlertDialog());
      }
      const { row: user } = await editLanguage({
        language: i18n.language,
      }).unwrap();

      if (statusCode === 201 && "account" in row) {
        const newestChangedDate = dayjs
          .utc(row.account.passwordChangePendedAt)
          .isAfter(row.account.passwordChangedAt)
          ? row.account.passwordChangePendedAt
          : row.account.passwordChangedAt;

        const isSafePassword = dayjs
          .utc(newestChangedDate)
          .add(90, "day")
          .isAfter(utcNow());

        // 비활성화 체크는 이미 위에서 완료됨 (line 273-277)

        // 1. 임시 비밀번호 발급상태 (로그인 5회 싪패, 첫 로그인)
        if (row.account.isTemporaryPassword) {
          dispatch(setTempUser({ ...user, language: i18n.language }));
          return goTo({
            path: "CHANGE_PASSWORD",
          });
        }

        // 3. 위 2가지 타입 외 대시보드 이동
        // 현재 Public Router 단에서 user가 있을시 대시보드 이동로직이 있음
        // 3-1 수입자
        // 3-2 수출자
        // 3-3 BOTH
        // 3-4 기업관리자, 중간관리자, 일반매니저
        if (isSafePassword) {
          dispatch(setUser({ ...user, language: i18n.language }));
          redirectUserByType({ ...user, language: i18n.language });
        } else {
          // 2. 90일 이후 비밀번호 변경 Alert
          dispatch(setTempUser(user));
          setAlertModalState(ALERT_MODAL_STATUS.PASSWORD_CHANGE_90DAYS_AFTER);
        }
      }
    } catch (e: any) {
      if (e.data?.errorCode === LOCKED_ACCOUNT_CODE) {
        return setAlertModalState(ALERT_MODAL_STATUS.LOCKED_ACCOUNT);
      }

      if (e.data?.errorCode === ACCOUNT_INACTIVATED_CODE) {
        try {
          const { row: user } = await editLanguage({
            language: i18n.language,
          }).unwrap();
          dispatch(setUser({ ...user, language: i18n.language }));
        } catch (editError) {
          console.error("Failed to fetch user info:", editError);
        }
        return navigate(PUBLIC_PATH.SIGNUP_PENDING_APPROVAL);
      }

      const errorCode = e.data?.errorCode;
      const i18nErrorMessages: Record<string, string> = {
        [EXPORTER_USER_EXPORTER_INACTIVATED_CODE]: t("error:exporterUserExporterInactivated"),
        [EXPORTER_USER_SUBSCRIPTION_NOT_AVAILABLE_CODE]: t("error:exporterUserSubscriptionNotAvailable"),
      };

      const message = i18nErrorMessages[errorCode] ??
        extractErrorMessage(e, t("error:unexpectedError"), i18n.language);

      alert.showAlert({
        message,
        type: "error",
      });
    }
  };

  const handlePasswordChangePending = async () => {
    if (!tempUser) return;
    try {
      await resetPassword().unwrap();
      dispatch(setUser(tempUser));
      dispatch(setTempUser(null));
    } catch (e: any) {
      alert.showAlert({
        message: extractErrorMessage(e, t("error:unexpectedError"), i18n.language),
        type: "error",
      });
    } finally {
      goTo({ path: "ROOT", option: { replace: true } });
    }
  };

  const handleAutoLogoutClose = () => {
    setAlertModalState(ALERT_MODAL_STATUS.NONE);
    localStorage.removeItem(localstorageKeySet.IS_SESSION_TIME_OUT);
  };

  const renderAlertDialog = () => {
    // 90일 이후 비밀번호 변경 Alert
    if (alertModalState === ALERT_MODAL_STATUS.PASSWORD_CHANGE_90DAYS_AFTER) {
      return (
        <AlertDialog
          title={t("common:changingPasswordAlertTitle")}
          description={
            <AlertDialogDescription>
              {t("common:changingPasswordAlertDescription")}
            </AlertDialogDescription>
          }
          onOk={() => {
            goTo({ path: "CHANGE_PASSWORD" });
          }}
          onCancel={handlePasswordChangePending}
          cancelText={t("common:changingPasswordAlertCancelText")}
          okText={t("common:changingPasswordAlertOkText")}
          open
          onOpenChange={(open) => {
            if (!open) {
              setAlertModalState(ALERT_MODAL_STATUS.NONE);
            }
          }}
        />
      );
    }
    // 계정 잠금 알림창
    if (alertModalState === ALERT_MODAL_STATUS.LOCKED_ACCOUNT) {
      return (
        <AlertDialog
          title={t("common:lockedAccountAlertTitle")}
          description={
            <AlertDialogDescription>
              {t("common:lockedAccountAlertDescription")}
            </AlertDialogDescription>
          }
          onOk={() => {
            goTo({ path: "FIND_PASSWORD" });
          }}
          onCancel={() => {
            setAlertModalState(ALERT_MODAL_STATUS.NONE);
          }}
          cancelText={t("common:lockedAccountCancelText")}
          okText={t("common:lockedAccountOkText")}
          open
          onOpenChange={(open) => {
            if (!open) {
              setAlertModalState(ALERT_MODAL_STATUS.NONE);
            }
          }}
        />
      );
    }
    // 자동 로그아웃 알림창
    if (alertModalState === ALERT_MODAL_STATUS.AUTO_LOGOUT) {
      return (
        <AutoLogoutAlertDialog
          open
          onOpenChange={() => handleAutoLogoutClose()}
          onClose={handleAutoLogoutClose}
        />
      );
    }
  };

  useEffect(() => idInputRef.current?.focus(), []);
  useEffect(() => {
    dispatch(setTempUser(null));
    // 로그인 페이지 진입 시 리다이렉트 상태 초기화
    dispatch(setLoginRedirectComplete(false));
  }, [dispatch]);

  useEffect(() => {
    if (isAutoLogoutDialogOpen) {
      setAlertModalState(ALERT_MODAL_STATUS.AUTO_LOGOUT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleContentLoadingOff();
  }, [handleContentLoadingOff]);

  return (
    <Layout ref={mainRef}>
      <LoginSection>
        <PublicPageHeader />

        <FormSection>
          <PageTitle>
            <Typo typoType="d3" as="h1" color="gray1">
              {t("signup:common.signIn.title")}
            </Typo>
          </PageTitle>

          <FormContainer>
            <StyledForm onSubmit={handleSubmit(handleLoginClick)} noValidate>
              <FormField $isError={!!errors.id}>
                <Typo typoType="b7r" as="label">
                  {t("common:id")}
                </Typo>
                <Controller
                  name="id"
                  control={control}
                  rules={{
                    required: true,
                    pattern: emailRegex,
                  }}
                  render={({ field }) => {
                    return (
                      <StyledInput
                        {...field}
                        required
                        type="text"
                        placeholder={t("common:placeholder.id")}
                        name="id"
                        autoComplete="email"
                        ref={idInputRef}
                        onClear={() => setValue("id", "")}
                        data-invalid={!!errors.id}
                        onInput={(e) => {
                          const input = e.target as HTMLInputElement;
                          input.value = input.value.replace(
                            /[\u3131-\u318E\uAC00-\uD7A3]/g,
                            ""
                          );
                        }}
                      />
                    );
                  }}
                />

                {errors.id?.type === "required" && (
                  <FormErrorMessage>
                    <Icon iconSrc={ErrorCircleSvg} iconSize={16} />
                    {t("error:login.emailValueMissing")}
                  </FormErrorMessage>
                )}
                {errors.id?.type === "pattern" && (
                  <FormErrorMessage>
                    <Icon iconSrc={ErrorCircleSvg} iconSize={16} />
                    {t("error:login.emailTypeMissMatching")}
                  </FormErrorMessage>
                )}
              </FormField>

              <FormField $isError={!!errors.password}>
                <Typo typoType="b7r" as="label">
                  {t("common:password")}
                </Typo>
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => {
                    return (
                      <StyledIconInput
                        {...field}
                        name="password"
                        type={passwordInputType}
                        autoComplete="current-password"
                        icon={
                          <StyledIcon
                            iconSrc={
                              passwordInputType === "password"
                                ? EyeOffSvg
                                : EyeSvg
                            }
                            iconSize={20}
                            onClick={() =>
                              setPasswordInputType((type) =>
                                type === "password" ? "text" : "password"
                              )
                            }
                          />
                        }
                        onClear={() => setValue("password", "")}
                        placeholder={t("common:placeholder.password")}
                      />
                    );
                  }}
                />

                {errors.password?.type === "required" && (
                  <FormErrorMessage>
                    <Icon iconSrc={ErrorCircleSvg} iconSize={16} />
                    {t("error:login.passwordValueMissing")}
                  </FormErrorMessage>
                )}
              </FormField>

              <MiddleDiv>
                <StyledLabel htmlFor="rememberId">
                  <Checkbox
                    checked={isRememberId}
                    id="rememberId"
                    onChange={() => setIsRememberId(!isRememberId)}
                  />
                  <Typo typoType="b9r">{t("common:rememberId")}</Typo>
                </StyledLabel>
                <TextButton
                  role="button"
                  onClick={() => goTo({ path: "FIND_PASSWORD" })}
                >
                  <Typo typoType="b9r" color="systemBlue2">
                    {t("common:findPassword")}
                  </Typo>
                </TextButton>
              </MiddleDiv>

              <LoginButton
                type="submit"
                isLoading={isSigningIn || isEditUser}
                disabled={isSigningIn || isEditUser}
              >
                <Typo typoType="btn3m" color="white">
                  {t("common:login")}
                </Typo>
              </LoginButton>
            </StyledForm>
            <SignUpRoadSection>
              <Typo typoType="b9m">{t("common:dontHaveAnAccount")}</Typo>

              <TextButton
                role="button"
                onClick={() => goTo({ path: "SIGNUP" })}
              >
                <Typo typoType="b9m" color="systemBlue2">
                  {t("common:signUp")}
                </Typo>
              </TextButton>
            </SignUpRoadSection>
          </FormContainer>
        </FormSection>

        <PublicPageFooter />
      </LoginSection>

      {renderAlertDialog()}
    </Layout>
  );
};

export default LoginPage;

const Layout = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
`;

const Section = styled.section`
  flex: 1;
`;

const LoginSection = styled(Section)`
  display: flex;
  flex-direction: column;
`;


const FormSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 60px 40px;
  flex: 1;
`;

const PageTitle = styled.div`
  padding: 24px 0;
  text-align: center;
  width: 100%;
  max-width: 1200px;

  h1 {
    margin: 0;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  max-width: 520px;
`;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;


const StyledInput = styled(Input)``;
const StyledIconInput = styled(IconInput)``;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div<{ $isError: boolean }>`
  ${({ $isError }) => $isError && css``};
  display: flex;
  flex-direction: column;
  gap: 8px;

  .icon-input-container {
    ${({ $isError }) =>
      $isError &&
      css`
        border: 1px solid ${colorSet.red2};
      `};
  }
`;

const FormErrorMessage = styled.span`
  ${typo.b9r};
  color: ${colorSet.red2};
  padding-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

const LoginButton = styled(Button)`
  text-align: center;
  margin-top: 16px;
`;

const MiddleDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SignUpRoadSection = styled.section`
  padding-top: 16px;
  display: flex;
  gap: 14px;
  justify-content: center;
`;


const AlertDialogDescription = styled.p`
  white-space: pre-wrap;
`;
